import { useMemo } from "react";
import { AnimatePresence, motion } from "motion/react";
import { OrganizationInitials } from "@/shared/components/OrganizationInitials";
import { OrganizationLogo } from "@/shared/components/OrganizationLogo";
import { useOrganizationInfo } from "@/shared/hooks/useOrganizationInfo";

type AnimatedOrganizationBrandProps = {
  isCollapsed: boolean
  compact?: boolean
};

const TRANSITION_CONFIG = {
  duration: 0.2,
  ease: [0.25, 0.46, 0.45, 0.94] as const,
};

const LOGO_CLASS_COMPACT = "h-16 max-h-16 w-auto";

export const AnimatedOrganizationBrand = ({ isCollapsed, compact = false }: AnimatedOrganizationBrandProps) => {
  // Pre-fetch organization data at top level to prevent loss during animations
  const { logo } = useOrganizationInfo();

  // Stabilize logo URL with memoization to persist during animation transitions
  const stableLogoUrl = useMemo(
    () => logo?.url || logo?.medium_url || null,
    [logo?.url, logo?.medium_url],
  );

  return (
    <div className="relative flex items-center justify-center">
      <AnimatePresence mode="wait" initial={false}>
        {isCollapsed
          ? (
            <motion.div
              key="initials"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={TRANSITION_CONFIG}
            >
              <OrganizationInitials />
            </motion.div>
          )
          : (
            <motion.div
              key="logo"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={TRANSITION_CONFIG}
            >
              <OrganizationLogo
                logoUrl={stableLogoUrl}
                className={compact ? LOGO_CLASS_COMPACT : undefined}
              />
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
};

AnimatedOrganizationBrand.displayName = "AnimatedOrganizationBrand";
